import React, { useCallback, useEffect, useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Drawer as DrawerAntD, message, Result as ResultAntD, Select, Table } from 'antd';
import moment, { Moment as MomentType } from 'moment';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import {
  counterpartLabel,
  detailLabel,
  eventLabel,
  eventMoment,
  EVENT_TYPE_OPTIONS,
  RESULT_STYLE,
  resultLabel,
} from './auditLabels';


import { downloadAuditCsv, getAuditEvents } from '../../../../hooks/interoperability';
import {
  AuditEvent,
  AuditEventType,
  AuditQuery,
  Connection,
} from '../../../../types/responses/Interoperability';
import StatusBadge from '../../../atoms/StatusBadge';
import { AuditEventDrawerDetails } from '../../../molecules/InteroperabilityDrawers';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { CellSub, Chevron, ClickableRows, ListCard } from '../styled';
import {
  defaultPeriod,
  formatCount,
  periodQuery,
} from '../transactionFormat';

const Filters = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const ExportButton = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 11.5px;

  &:disabled {
    color: #a9b4c2;
    cursor: not-allowed;
  }
`;

const Moment = styled.div`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const Foot = styled.div`
  border-top: 1px solid #e9ebee;
  background: #fafbfc;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11.5px;
  color: #6b7280;
`;

const Note = styled.span`
  flex: 1;
`;

const { RangePicker } = DatePicker;

/** O Drawer do antd nao declara children no typing desta versao. */
const Drawer: any = DrawerAntD;

/** Dez por pagina, como as outras listagens do modulo. */
const PAGE_SIZE = 10;

export type HistoryTabProps = {
  tenantId: string;

  /** Parceiros com contrato, para o filtro. */
  connections: Connection[];
};

/**
 * Aba Histórico (MV2-1718).
 *
 * A trilha é só leitura — a API não oferece escrita nem remoção, porque uma
 * trilha que pode ser reescrita não serve como trilha. A tela não tem, e não
 * pode ganhar, nenhum botão de editar ou apagar evento.
 */
export const HistoryTab: React.FC<HistoryTabProps> = ({ tenantId, connections }) => {
  const [period] = useState(() => defaultPeriod());

  const [eventType, setEventType] = useState<AuditEventType | ''>('');
  const [partnerTenantId, setPartnerTenantId] = useState('');
  const [from, setFrom] = useState(period.from.slice(0, 10));
  const [to, setTo] = useState(period.to.slice(0, 10));

  const [detail, setDetail] = useState<AuditEvent | null>(null);

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);

  const query: AuditQuery = {
    ...(eventType ? { eventTypes: [eventType] } : {}),
    ...(partnerTenantId ? { partnerTenantId } : {}),
    ...periodQuery(from, to),
  };

  const queryKey = JSON.stringify(query);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);

      const parsed: AuditQuery = JSON.parse(queryKey);
      const result = await getAuditEvents(tenantId, parsed, page, PAGE_SIZE);

      setEvents(result?.content || []);
      setTotalElements(result?.totalElements || 0);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId, queryKey, page]);

  useEffect(() => {
    load();
  }, [load]);

  /** Trocar filtro volta para a primeira página: a atual pode não existir mais. */
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      await downloadAuditCsv(tenantId, query);
    } catch {
      // Aviso proprio, e nao o `loadError`: aquele e o estado de falha da CARGA, e o
      // render esconde a lista inteira atras dele. Falhar o download apagava da tela as
      // linhas que tinham carregado bem, com uma frase falsa sobre elas.
      message.error(T('Could not download the CSV. The list on screen is unchanged.'));
    } finally {
      setExporting(false);
    }
  };


  /**
   * Colunas no formato do antd Table, como o resto do dashboard. A tabela era
   * `<table>` cru, o que fazia esta aba nao parecer do mesmo modulo que Conexoes.
   */
  const columns = [
    {
      title: T('Date / time'),
      key: 'moment',
      render: (_: unknown, event: AuditEvent) => <Moment>{eventMoment(event)}</Moment>,
    },
    {
      title: T('Event'),
      key: 'event',
      render: (_: unknown, event: AuditEvent) => {
        const detail = detailLabel(event);

        return (
          <div>
            {eventLabel(event)}
            {detail && <CellSub>{detail}</CellSub>}
          </div>
        );
      },
    },
    {
      title: T('Responsible'),
      key: 'actor',
      render: (_: unknown, event: AuditEvent) => (
        <div>
          {event.actor || T('no human author')}
          {event.ip && (
            <CellSub>
              <Mono>{event.ip}</Mono>
            </CellSub>
          )}
        </div>
      ),
    },
    {
      title: T('Partner'),
      key: 'partner',
      render: (_: unknown, event: AuditEvent) => counterpartLabel(event),
    },
    {
      title: T('Result'),
      key: 'result',
      render: (_: unknown, event: AuditEvent) => {
        const style = RESULT_STYLE[event.result];

        return (
          <StatusBadge
            tone={{
              bg: style?.bg || '#eef0f2',
              fg: style?.fg || '#6b7280',
              dot: style?.dot || '#9aa3af',
            }}
          >
            {resultLabel(event)}
          </StatusBadge>
        );
      },
    },
    {
      /* A seta que avisa que a linha abre detalhe, como Conexoes e API. */
      title: '',
      key: 'open',
      width: 40,
      render: () => (
        <Chevron>
          <RightOutlined />
        </Chevron>
      ),
    },
  ];

  return (
    <ClickableRows>
      <Filters>
        {/* Select do antd: busca e o foco tingido pelo tema, como o resto do dash. */}
        <Select
          value={eventType}
          style={{ minWidth: 220 }}
          showSearch
          optionFilterProp="children"
          onChange={(value: AuditEventType | '') => changeFilter(() => setEventType(value))}
        >
          <Select.Option value="">{T('All events')}</Select.Option>
          {EVENT_TYPE_OPTIONS().map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>

        <Select
          value={partnerTenantId}
          style={{ minWidth: 200 }}
          showSearch
          optionFilterProp="children"
          onChange={(value: string) => changeFilter(() => setPartnerTenantId(value))}
        >
          <Select.Option value="">{T('All partners')}</Select.Option>
          {connections.map((connection) => (
            <Select.Option key={connection.partner_tenant_id} value={connection.partner_tenant_id}>
              {connection.partner_name}
            </Select.Option>
          ))}
        </Select>

        {/*
          RangePicker do antd com formato DD/MM/YYYY, como o resto do dashboard. O
          input[type=date] nativo mostrava a data no formato do sistema operacional —
          quem usa o SO em ingles via mm/dd/yyyy no meio de um dash em portugues.
        */}
        <RangePicker
          format="DD/MM/YYYY"
          allowClear={false}
          value={[moment(from), moment(to)]}
          onChange={(range) =>
            changeFilter(() => {
              const [start, end] = (range || []) as MomentType[];

              if (start && end) {
                setFrom(start.format('YYYY-MM-DD'));
                setTo(end.format('YYYY-MM-DD'));
              }
            })
          }
        />

        <Spacer />

        <ExportButton type="button" onClick={exportCsv} disabled={exporting || !totalElements}>
          {exporting ? T('Preparing...') : T('Download CSV')}
        </ExportButton>
      </Filters>

      {loadError && (
        <ResultAntD
          status="warning"
          title={T('Could not load the history.')}
          extra={
            <Button type="primary" onClick={load}>
              {T('Try again')}
            </Button>
          }
        />
      )}

      {loading && <ListSkeleton />}

      {!loading && !loadError && (
        <ListCard>
          <Table
            rowKey="audit_id"
            locale={{ emptyText: T('No events in this period.') }}
            pagination={false}
            columns={columns}
            dataSource={events}
            scroll={{ x: true }}
            rowClassName="interop-row"
            onRow={(item: AuditEvent) => ({
              onClick: () => setDetail(item),
            })}
          />

          <Foot>
            <Note>{T('Read-only trail: events cannot be edited or deleted.')}</Note>
            <span>
              {formatCount(totalElements)} {T('events')}
            </span>
          </Foot>

          {/* Paginador do dashboard; ele e 1-based e a API 0-based. */}
          <Pagination
            currentPage={page + 1}
            pageSize={PAGE_SIZE}
            totalCount={totalElements}
            onPageChange={(next: number) => setPage(next - 1)}
          />
        </ListCard>
      )}
      <Drawer
        width={420}
        visible={detail !== null}
        title={detail ? eventLabel(detail) : ''}
        onClose={() => setDetail(null)}
      >
        {detail && <AuditEventDrawerDetails event={detail} />}
      </Drawer>
    </ClickableRows>
  );
};
